import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { TransactionDetailsQuery } from './query';

const GET_TRANSACTIONS = gql`
  query getTransactions(
    $type: String
    $firebaseId: String
    $municipalityId: String
    $superAdmin: Boolean
    $filters: FiltersTypeInput
    $page: Int!
    $length: Int!
    $sort: String
    $search: String
    $offSetData: String
  ) {
    getTransactions(
      type: $type
      firebaseId: $firebaseId
      municipalityId: $municipalityId
      superAdmin: $superAdmin
      filters: $filters
      page: $page
      length: $length
      sort: $sort
      search: $search
      offSetData: $offSetData
    ) {
      success
      message
      totalPages
      totalRecords
      currentPage
      transaction {
        status
        transactionCode
        createdBy
        firebaseId
        pendingTasks
        updatedAt
        id
        property {
          type
          location
          address
          city
          state
          zipCode
          purchasePrice
          closingDate
          notes
        }
      }
    }
  }
`;

export function useGetTransactionsQuery(variables: {
  type: string;
  firebaseId: string;
  municipalityId: string;
  superAdmin: boolean;
  offSetData: string;
  filters: {
    municipalities: string;
    createdDateRange: {
      startDate: string;
      endDate: string;
    };
    status: string;
  };
  page: number;
  length: number;
  sort: string;
  search: string;
}) {
  return useQuery(GET_TRANSACTIONS, {
    variables,
    fetchPolicy: 'no-cache',
  });
}

const GET_QR_TRANSACTION_DETAILS = gql`
 query getPdfQRCodeTransactionDetails($id:String!){
  getPdfQRCodeTransactionDetails(id: $id) {
    ${TransactionDetailsQuery}
  }
 }
`;
export function useGetPdfQRCodeTransactionDetails(variables: { id: string }) {
  return useQuery(GET_QR_TRANSACTION_DETAILS, {
    variables,
    fetchPolicy: 'no-cache',
  });
}

const GET_ALL_ACTIVE_STATES = gql`
  query getAllActiveStates {
    getAllActiveStates {
      success
      message
      states {
        _id
        label
        value
        postalAbbrevation
      }
    }
  }
`;
export function useGetAllActiveStates() {
  return useQuery(GET_ALL_ACTIVE_STATES);
}

const GET_TRANSACTION = gql`
  query getTransactionDetails($id: String!, $offSetData: String) {
    getTransactionDetails(id: $id, offSetData: $offSetData) {
      success
      message
      transaction {
        id
        transactionCode
        municipalityId
        approvedBy
        approvedDate
        isApproved
        mlsLink
        approvedInfo {
          company
          email
          firstName
          lastName
          phone
        }
        municipalityData {
          name
          logo
          transferDocumentTitle
          includeLegalDescription
          inspectionDaysMin
          taxExemption
          counties {
            _id
            name
            label
            value
            status
          }
          waterReading {
            required
            instructions
          }
          TaxCalculations {
            rate
            instructions
          }
          inspection {
            required
            instructions
            allowUsersToUploadDocs
          }
          proofofTaxPayment {
            required
            instructions
          }
          qualifyingExemptions {
            id
            exemption
          }
          additionalQuestions {
            id
            municipalityId
            question
            serialNo
            status
            deleted
          }
        }
        titleCompany
        titleCompanyId
        titleCompanyData {
          _id
          name
        }
        creator {
          type
          firebaseId
          user {
            firstName
            lastName
            company
            email
            phone
          }
        }
        transactionDocuments {
          docUniqueId
          documentType
          otherDocumentType
          docContentType
          firebaseId
          title
          notes
          docUrl
          docKey
          uploadedDate
          uploadedBy
        }
        transactionPayments {
          _id
          transactionId
          firebaseId
          userEmail
          paymentStatus
          paidAmount
          paymentMethodsIdorPaymentId
          stripePaymentId
          cardType
          cardBrand
          cardHolderName
          dateofPayment
          cardEnding
          costItem
          cost
        }
        property {
          type
          location
          address
          city
          state
          zipCode
          longitude
          latitude
          purchasePrice
          closingDate
          notes
          stateId
          pin
          countyId
        }
        stateInfo {
          label
          value
          _id
        }
        countyInfo {
          _id
          value
          label
          status
        }
        buyers {
          name
          address
          email
          phone
          showEntityCheckbox
          showRadioButtons
          subDetails {
            contactNameForBusiness
            isEntity
            isIndividual
          }
        }
        sellers {
          name
          address
          email
          phone
          showEntityCheckbox
          showRadioButtons
          subDetails {
            contactNameForBusiness
            isEntity
            isIndividual
          }
        }
        activityLog {
          firebaseId
          user {
            firstName
            lastName
            company
            email
            phone
          }
          item
          title
          description
          date
          status
        }
        assignmentChecklist {
          legalDescription {
            firebaseId
            status
            completedAt
            userType
            notes
            user {
              firstName
              lastName
            }
          }
          verifyTaxCalculations {
            firebaseId
            status
            docSrc
            docName
            userType
            completedAt
            user {
              firstName
              lastName
            }
            taxAmount
            verified
            exemption {
              reasons {
                id
                checked
                exemption
              }
              details
              exemptionDocuments {
                name
                src
                uploadedDate
              }
            }
          }

          waterReading {
            firebaseId
            status
            docSrc
            docName
            userType
            completedAt
            isFinalReadingAvailNow
            isFinalReadingRequested
            dateForFinalReading
            readingDocuments {
              name
              src
              uploadedDate
            }
            user {
              firstName
              lastName
            }
          }
          inspection {
            firebaseId
            status
            docSrc
            docName
            userType
            completedAt
            user {
              firstName
              lastName
            }
            inspectionNotes
            inspectionDocuments {
              name
              src
              uploadedDate
            }
          }

          additionalQuestions {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
            answers {
              question
              questionId
              answer
              answeredDate
            }
          }
          proofofTaxPayment {
            firebaseId
            status
            docSrc
            docName
            userType
            completedAt
            user {
              firstName
              lastName
            }
            taxPaymentReceiptNumber
            dateofTaxPayment
            proofofTaxPaymentDocuments {
              name
              src
              uploadedDate
            }
          }
        }
        status
        updatedAt
        createdAt
        pendingTasks
        creator {
          type
          firebaseId
        }
        createdUserData {
          firstName
          lastName
        }
        professionalUsers {
          type
          firebaseId
          profUserTypeId
          key
          user {
            firstName
            lastName
            company
            email
            phone
            firebaseId
            profilePicUrl
          }
        }
      }
    }
  }
`;

export function useGetTransactionQuery(variables: {
  id: string;
  offSetData: string;
}) {
  return useQuery(GET_TRANSACTION, {
    variables,
    fetchPolicy: 'no-cache',
  });
}

const GET_PROFESSIONAL_TYPES = gql`
  query getAllActiveTransactionCreateProfessionalUserTypes {
    getAllActiveTransactionCreateProfessionalUserTypes {
      success
      message
      professionalUserTypes {
        _id
        name
        label
        value
        key
        status
      }
    }
  }
`;
export function useGetProfessionalTypes() {
  return useQuery(GET_PROFESSIONAL_TYPES);
}

const GET_USERMUNICIPALITIES = gql`
  query getUserMunicipalityList($firebaseId: String!) {
    getUserMunicipalityList(firebaseId: $firebaseId) {
      success
      message
      municipality {
        municipalityId
        municipalityName
      }
    }
  }
`;
export function useGetUserMunicipalitiesQuery(variables: {
  firebaseId: string;
}) {
  return useQuery(GET_USERMUNICIPALITIES, {
    variables,
    fetchPolicy: 'no-cache',
  });
}

const GET_ACTIVE_MUNICIPALITIES = gql`
  mutation getActiveMunicipalitiesByState($stateId: String!) {
    getActiveMunicipalitiesByState(stateId: $stateId) {
      success
      message
      municipality {
        id
        name
        status
        city
        stateId
        includeLegalDescription
        inspectionDaysMin
        waterReading {
          required
          instructions
        }
        TaxCalculations {
          rate
          instructions
        }
        stateInfo {
          label
          value
        }
        counties {
          _id
          value
          label
          status
        }
        inspection {
          required
          instructions
          allowUsersToUploadDocs
        }
        proofofTaxPayment {
          required
          instructions
        }
        additionalQuestions {
          id
        }
      }
    }
  }
`;

export function useGetActiveMunicipalitiesQuery() {
  return useMutation(GET_ACTIVE_MUNICIPALITIES, {
    // fetchPolicy: 'no-cache',
  });
}
const GET_IMAGE_DATA = gql`
  mutation getMunicipalityLogoData($sThreeUrl: String) {
    transactionPayment(sThreeUrl: $sThreeUrl) {
      success
      message
      bodyData
    }
  }
`;
export function useGetImageData() {
  return useMutation(GET_IMAGE_DATA);
}
const EDIT_TRANSACTION = gql`
  mutation editTransaction(
    $id: String!
    $municipalityId: String!
    $property: PropertyData
    $buyers: [BuyerOrSellerInput]
    $sellers: [BuyerOrSellerInput]
    $professionalUsers: [UserInfoInput]
    $assignmentChecklist: AssignmentChecklistInput
    $activityLog: [ActivityLogInput]
    $status: String!
    $transactionDocuments: [TransactionDocumentsInput]
    $titleCompany: String
    $titleCompanyId: String
    $transactionPaymentsInfo: [TransactionPaymentsInfoInput]
    $mlsLink: String
  ) {
    editTransaction(
      id: $id
      municipalityId: $municipalityId
      property: $property
      buyers: $buyers
      sellers: $sellers
      professionalUsers: $professionalUsers
      assignmentChecklist: $assignmentChecklist
      activityLog: $activityLog
      status: $status
      transactionDocuments: $transactionDocuments
      titleCompany: $titleCompany
      titleCompanyId: $titleCompanyId
      transactionPaymentsInfo: $transactionPaymentsInfo
      mlsLink: $mlsLink
    ) {
      success
      message
      transaction {
        id
        status
        updatedAt
        createdAt
        pendingTasks
        titleCompany
        titleCompanyId
        mlsLink
        municipalityData {
          name
          logo
        }
        creator {
          type
          firebaseId
          user {
            firstName
            lastName
            company
            email
            phone
          }
        }
        property {
          type
          location
          address
          city
          state
          zipCode
          longitude
          latitude
          purchasePrice
          closingDate
          notes
        }
        buyers {
          name
          address
          email
          phone
          showEntityCheckbox
          showRadioButtons
          subDetails {
            contactNameForBusiness
            isEntity
            isIndividual
          }
        }
        sellers {
          name
          address
          email
          phone
          showEntityCheckbox
          showRadioButtons
          subDetails {
            contactNameForBusiness
            isEntity
            isIndividual
          }
        }
        assignmentChecklist {
          legalDescription {
            firebaseId
            status
            completedAt
            userType
            user {
              firstName
              lastName
            }
          }
          verifyTaxCalculations {
            firebaseId
            status
            completedAt
            userType
            user {
              firstName
              lastName
            }
          }
          waterReading {
            firebaseId
            status
            completedAt
            userType
            user {
              firstName
              lastName
            }
          }
          inspection {
            firebaseId
            status
            completedAt
            userType
            user {
              firstName
              lastName
            }
          }
          additionalQuestions {
            firebaseId
            status
            completedAt
            userType
            user {
              firstName
              lastName
            }
          }
        }
        creator {
          type
          firebaseId
        }
        transactionDocuments {
          docUniqueId
          documentType
          otherDocumentType
          docContentType
          firebaseId
          title
          notes
          docUrl
          docKey
          uploadedDate
          uploadedBy
        }
        createdUserData {
          firstName
          lastName
        }
        professionalUsers {
          type
          firebaseId
          user {
            firstName
            lastName
          }
        }
      }
    }
  }
`;
export function useEditTransactionMutation() {
  return useMutation(EDIT_TRANSACTION);
}

const CREATE_TRANSACTION = gql`
  mutation createTransaction(
    $municipalityId: String!
    $createdBy: String!
    $property: PropertyData
    $creator: UserInfoInput
    $buyers: [BuyerOrSellerInput]
    $sellers: [BuyerOrSellerInput]
    $professionalUsers: [UserInfoInput]
    $assignmentChecklist: AssignmentChecklistInput
    $activityLog: [ActivityLogInput]
    $status: String!
    $transactionDocuments: [TransactionDocumentsInput]
    $titleCompany: String
    $titleCompanyId: String
    $transactionPaymentsInfo: [TransactionPaymentsInfoInput]
    $mlsLink: String
  ) {
    createTransaction(
      municipalityId: $municipalityId
      createdBy: $createdBy
      property: $property
      creator: $creator
      buyers: $buyers
      sellers: $sellers
      professionalUsers: $professionalUsers
      assignmentChecklist: $assignmentChecklist
      activityLog: $activityLog
      status: $status
      transactionDocuments: $transactionDocuments
      titleCompany: $titleCompany
      titleCompanyId: $titleCompanyId
      transactionPaymentsInfo: $transactionPaymentsInfo
      mlsLink: $mlsLink
    ) {
      success
      message
      transaction {
        id
        transactionCode
        status
        updatedAt
        createdAt
        pendingTasks
        mlsLink
        titleCompany
        titleCompanyId
        countyInfo {
          label
          value
        }
        municipalityData {
          name
          logo
        }
        creator {
          type
          firebaseId
          user {
            firstName
            lastName
            company
            email
            phone
          }
        }
        property {
          type
          location
          address
          city
          state
          stateId
          zipCode
          longitude
          latitude
          purchasePrice
          closingDate
          notes
          pin
          countyId
        }
        buyers {
          name
          address
          email
          phone
          showEntityCheckbox
          showRadioButtons
          subDetails {
            contactNameForBusiness
            isEntity
            isIndividual
          }
        }
        sellers {
          name
          address
          email
          phone
          showEntityCheckbox
          showRadioButtons
          subDetails {
            contactNameForBusiness
            isEntity
            isIndividual
          }
        }
        assignmentChecklist {
          legalDescription {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
          }
          verifyTaxCalculations {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
          }
          waterReading {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
          }
          inspection {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
          }
          additionalQuestions {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
          }
        }
        creator {
          type
          firebaseId
        }
        createdUserData {
          firstName
          lastName
        }
        professionalUsers {
          type
          firebaseId
          user {
            firstName
            lastName
          }
        }
        transactionDocuments {
          docUniqueId
          documentType
          otherDocumentType
          docContentType
          firebaseId
          title
          notes
          docUrl
          docKey
          uploadedDate
          uploadedBy
        }
      }
    }
  }
`;
const UPDATE_DOCUMENTS = gql`
  mutation updateTransactionDocuments(
    $transactionId: String!
    $transactionDocuments: [TransactionDocumentsInput]
    $activityLog: [ActivityLogInput]
  ) {
    updateTransactionDocuments(
      transactionId: $transactionId
      transactionDocuments: $transactionDocuments
      activityLog: $activityLog
    ) {
      success
      message
      transaction {
        id
        municipalityId
        transactionCode
        pendingTasks
        status
        updatedAt
        completedAt
        transactionDocuments {
          docUniqueId
          otherDocumentType
          docContentType
          documentType
          firebaseId
          title
          notes
          docUrl
          docKey
          uploadedDate
          uploadedBy
        }
      }
    }
  }
`;

export function useUpdateDocuments() {
  return useMutation(UPDATE_DOCUMENTS);
}
export function useCreateTransactionMutation() {
  return useMutation(CREATE_TRANSACTION);
}

const UPDATE_CHECKLIST = gql`
  mutation updateTransactionChecklist(
    $transactionId: String!
    $status: String!
    $userType: String
    $completedAt: String!
    $assignmentChecklistKey: String!
    $notes: String
    $firebaseId: String!
    $isFinalReadingAvailNow: String
    $isFinalReadingRequested: String
    $dateForFinalReading: String
    $readingDocuments: [ReadingDocumentsInput]
    $answers: [AnswersInput]
    $taxPaymentReceiptNumber: String
    $dateofTaxPayment: String
    $proofofTaxPaymentDocuments: [ReadingDocumentsInput]
    $inspectionNotes: String
    $inspectionDocuments: [ReadingDocumentsInput]
    $activityLog: [ActivityLogInput]
    $taxAmount: Float
    $verified: Boolean
    $exemptionReasons: [exemptionReasonsInput]
    $exemptionDetails: String
    $exemptionDocuments: [ReadingDocumentsInput]
    $isDelete: Boolean
    $paymentId: String
    $cost: String
    $costItem: String
  ) {
    updateTransactionChecklist(
      transactionId: $transactionId
      status: $status
      userType: $userType
      completedAt: $completedAt
      assignmentChecklistKey: $assignmentChecklistKey
      notes: $notes
      firebaseId: $firebaseId
      isFinalReadingAvailNow: $isFinalReadingAvailNow
      isFinalReadingRequested: $isFinalReadingRequested
      dateForFinalReading: $dateForFinalReading
      readingDocuments: $readingDocuments
      answers: $answers
      taxPaymentReceiptNumber: $taxPaymentReceiptNumber
      dateofTaxPayment: $dateofTaxPayment
      proofofTaxPaymentDocuments: $proofofTaxPaymentDocuments
      inspectionNotes: $inspectionNotes
      inspectionDocuments: $inspectionDocuments
      activityLog: $activityLog
      taxAmount: $taxAmount
      verified: $verified
      exemptionReasons: $exemptionReasons
      exemptionDetails: $exemptionDetails
      exemptionDocuments: $exemptionDocuments
      isDelete: $isDelete
      paymentId: $paymentId
      cost: $cost
      costItem: $costItem
    ) {
      success
      message
      transaction {
        id
        status
        pendingTasks
        updatedAt
        createdAt
        municipalityId
        creator {
          type
          firebaseId
          user {
            firstName
            lastName
            company
            email
            phone
          }
        }
        property {
          type
          location
          address
          city
          state
          zipCode
          purchasePrice
          closingDate
          notes
        }
        buyers {
          name
          address
          email
          phone
        }
        sellers {
          name
          address
          email
          phone
        }
        activityLog {
          firebaseId
          user {
            firstName
            lastName
            company
            email
            phone
          }
          item
          title
          description
          date
          status
        }
        assignmentChecklist {
          legalDescription {
            firebaseId
            status
            userType
            completedAt
            notes
            user {
              firstName
              lastName
            }
          }
          verifyTaxCalculations {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
            exemption {
              reasons {
                id
                checked
                exemption
              }
              details
              exemptionDocuments {
                name
                src
                uploadedDate
              }
            }
          }
          waterReading {
            firebaseId
            status
            userType
            completedAt
            isFinalReadingAvailNow
            isFinalReadingRequested
            dateForFinalReading
            readingDocuments {
              name
              src
              uploadedDate
            }
            user {
              firstName
              lastName
            }
          }
          inspection {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
            inspectionNotes
            inspectionDocuments {
              name
              src
              uploadedDate
            }
          }
          additionalQuestions {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
            answers {
              question
              questionId
              answer
              answeredDate
            }
          }
          verifyTaxCalculations {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
            taxAmount
            verified
          }
          proofofTaxPayment {
            firebaseId
            status
            userType
            completedAt
            user {
              firstName
              lastName
            }
            taxPaymentReceiptNumber
            dateofTaxPayment
            proofofTaxPaymentDocuments {
              name
              src
              uploadedDate
            }
          }
        }
        creator {
          type
          firebaseId
        }
        createdUserData {
          firstName
          lastName
        }
        professionalUsers {
          type
          firebaseId
          user {
            firstName
            lastName
          }
        }
      }
    }
  }
`;
export function useUpdateChecklistMutation() {
  return useMutation(UPDATE_CHECKLIST);
}

const MARK_AS_COMPLETED = gql`
  mutation updateTransactionChecklist(
    $transactionId: String!
    $completed: String!
    $activityLog: [ActivityLogInput]
  ) {
    markasCompelted(
      transactionId: $transactionId
      completed: $completed
      activityLog: $activityLog
    ) {
      success
      message
      transaction {
        id
        status
        activityLog {
          firebaseId
          user {
            firstName
            lastName
            company
            email
            phone
          }
          item
          title
          description
          date
          status
        }
        completed
        completedAt
      }
    }
  }
`;

export function useMarkasCompleted() {
  return useMutation(MARK_AS_COMPLETED);
}
const MARK_AS_APPROVED = gql`
  mutation markasApproved(
    $transactionId: String!
    $approvedBy: String!
    $isApproved: Boolean!
    $status: String!
    $activityLog: [ActivityLogInput]
  ) {
    markasApproved(
      transactionId: $transactionId
      approvedBy: $approvedBy
      isApproved: $isApproved
      status: $status
      activityLog: $activityLog
    ) {
      success
      message
      transaction {
        isApproved
        approvedBy
        approvedDate
        id
        municipalityId
        transactionCode
        pendingTasks
        status
        updatedAt
        municipalityData {
          id
          name
          transferDocumentTitle
        }
        assignmentChecklist {
          verifyTaxCalculations {
            docSrc
            docName
          }
          waterReading {
            docSrc
            docName
          }
          inspection {
            docSrc
            docName
          }
          proofofTaxPayment {
            docSrc
            docName
          }
        }
        transactionPayments {
          _id
          firebaseId
          transactionId
          userEmail
          costItem
          cost
          paymentStatus
          paidAmount
          cardHolderName
          paymentId
          dateofPayment
          cardEnding
        }

        titleCompany
        titleCompanyId
        titleCompanyData {
          _id
          name
        }
        completedAt
        transactionDocuments {
          docUrl
          docKey
          docUniqueId
          firebaseId
          notes
          documentType
          uploadedDate
          uploadedBy
        }
      }
    }
  }
`;
export function useMarkasApproved() {
  return useMutation(MARK_AS_APPROVED);
}

const GET_UPLOAD_DOC_TYPES = gql`
  query getAllUploadDocType {
    getAllUploadDocType {
      success
      message
      docTypes {
        name
        docType
      }
    }
  }
`;
export function useGetUploadDocTypes() {
  return useQuery(GET_UPLOAD_DOC_TYPES);
}

const GET_PROPERTIES = gql`
  query getAllActivePropertyTypes {
    getAllActivePropertyTypes {
      success
      message
      propertyTypes {
        name
        _id
      }
    }
  }
`;

export function useGetProperties() {
  return useQuery(GET_PROPERTIES);
}

const GET_TITLE_COMPANIES = gql`
  query getAllActiveTitleCompanies {
    getAllActiveTitleCompanies {
      success
      message
      titleCompanies {
        _id
        name
      }
    }
  }
`;

export function useGetTitleCompanies() {
  return useQuery(GET_TITLE_COMPANIES);
}
const TRANSACTION_PAYMENT = gql`
  mutation transactionPayment(
    $newTransactionPaymentsInfo: [TransactionPaymentsInfoInput]
    $transactionPaymentsInfoIds: [String]
    $paidAmount: Float
    $cardHolderName: String
    $paymentId: String
    $dateofPayment: String
    $cardEnding: Int
    $activityLog: [ActivityLogInput]
    $transactionId: String!
    $firebaseId: String!
    $userEmail: String!
    $cardBrand: String
    $cardType: String
  ) {
    transactionPayment(
      transactionId: $transactionId
      userEmail: $userEmail
      firebaseId: $firebaseId
      paidAmount: $paidAmount
      newTransactionPaymentsInfo: $newTransactionPaymentsInfo
      cardHolderName: $cardHolderName
      dateofPayment: $dateofPayment
      cardEnding: $cardEnding
      paymentId: $paymentId
      activityLog: $activityLog
      transactionPaymentsInfoIds: $transactionPaymentsInfoIds
      cardBrand: $cardBrand
      cardType: $cardType
    ) {
      success
      message
      transactionPaymentsInfo {
        _id
        firebaseId
        transactionId
        userEmail
        costItem
        cost
        paymentStatus
        paidAmount
        cardHolderName
        dateofPayment
        cardEnding
        paymentMethodsIdorPaymentId
        stripePaymentId
        cardType
        cardBrand
      }
    }
  }
`;
export function useTransactionPayment() {
  return useMutation(TRANSACTION_PAYMENT);
}

const TRANSACTION_SEARCH = gql`
  mutation transactionSearch(
    $firebaseId: String
    $municipalityId: String
    $superAdmin: Boolean
    $search: String
    $page: Int!
    $limit: Int!
    $offSetData: String
  ) {
    transactionSearch(
      firebaseId: $firebaseId
      municipalityId: $municipalityId
      superAdmin: $superAdmin
      search: $search
      page: $page
      limit: $limit
      offSetData: $offSetData
    ) {
      success
      message
      totalRecords
      totalPages
      currentPage
      transaction {
        id
        transactionCode
        property {
          type
          location
          address
          city
          state
          zipCode
          purchasePrice
          closingDate
          notes
          latitude
          longitude
        }
        transactionDocuments {
          documentType
          otherDocumentType
          firebaseId
          title
          notes
          docUrl
          docKey
          docUniqueId
          docContentType
          uploadedDate
          uploadedBy
        }
        status
        createdBy
        firebaseId
        pendingTasks
        updatedAt
      }
    }
  }
`;
export function useTransactionSearch() {
  return useMutation(TRANSACTION_SEARCH);
}
